import { ChevronRight } from "lucide-react";

import { Badge } from "components/ui/badge";

import { DEFAULT_TRACK_COLOR } from "./AgendaSchedule";
import { initials } from "./initials";
import type { DirectoryAttendee, Track } from "./schemas";
import type { ResolvedSignal } from "./signal-utils";

// The three categorical Dusk gradients, each with white initials. A track takes
// one by its position in the track list, so the same track is the same colour
// on every card. Anybody without a known track gets the muted surface.
const AVATAR_FILLS = [
  "bg-dusk-tile-a text-white",
  "bg-dusk-tile-b text-white",
  "bg-dusk-chapter-2 text-white",
] as const;
const NEUTRAL_FILL = "bg-muted text-muted-foreground";

export function avatarFill(trackIndex: number | undefined): string {
  return trackIndex === undefined
    ? NEUTRAL_FILL
    : AVATAR_FILLS[trackIndex % AVATAR_FILLS.length];
}

/**
 * One person in the directory: the whole card is the button that opens their
 * profile sheet. Its accessible name is its text (name, role and company,
 * track, signal). The name now also carries the track, because the track is a line on the card.
 */
export function AttendeeCard({
  attendee,
  track,
  trackIndex,
  picks,
  highlighted,
  onSelect,
}: {
  attendee: DirectoryAttendee;
  /** Undefined when the person has none, or it no longer exists. */
  track: Track | undefined;
  trackIndex: number | undefined;
  picks: ResolvedSignal[];
  highlighted: ReadonlySet<string>;
  onSelect: () => void;
}) {
  const shown =
    highlighted.size > 0
      ? picks.filter((pick) => highlighted.has(pick.id))
      : picks.slice(0, 1);
  return (
    <button
      type="button"
      onClick={onSelect}
      // `w-full min-w-0 wrap-anywhere`: a button is sized to its content, and
      // one long name or company widened the column past a phone's viewport.
      className="flex min-h-[5.25rem] w-full min-w-0 items-center gap-3.5 rounded-[22px] border border-border bg-card px-3.5 py-3 text-left text-card-foreground shadow-card transition-shadow wrap-anywhere hover:shadow-card-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      data-testid={`attendee-card-${attendee.id}`}
    >
      <AttendeeAvatar
        attendee={attendee}
        fill={avatarFill(track ? trackIndex : undefined)}
      />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="block font-display text-[1.3125rem] leading-[1.12] text-foreground">
          {attendee.name}
        </span>
        {(attendee.role || attendee.company) && (
          <span className="block text-sm text-muted-foreground">
            {[attendee.role, attendee.company].filter(Boolean).join(" · ")}
          </span>
        )}
        {track && (
          <span
            className="flex items-center gap-1.5 pt-0.5"
            data-testid={`attendee-track-${attendee.id}`}
          >
            <span
              aria-hidden="true"
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: track.color ?? DEFAULT_TRACK_COLOR }}
            />
            <span className="min-w-0 text-[0.8125rem] font-semibold text-muted-foreground">
              {track.name}
            </span>
          </span>
        )}
        {shown.length > 0 && (
          <span
            className="mt-1 flex min-w-0 flex-wrap gap-1"
            data-testid={`badge-signal-${attendee.id}`}
          >
            {shown.map((pick) => (
              <Badge
                key={pick.id}
                variant="outline"
                className="max-w-full min-w-0 shrink whitespace-normal wrap-anywhere rounded-lg text-left"
              >
                {pick.label}
              </Badge>
            ))}
          </span>
        )}
      </span>
      <ChevronRight
        aria-hidden="true"
        className="h-5 w-5 shrink-0 text-muted-foreground"
      />
    </button>
  );
}

function AttendeeAvatar({
  attendee,
  fill,
}: {
  attendee: DirectoryAttendee;
  fill: string;
}) {
  const size = "h-14 w-14 shrink-0 rounded-full";
  if (attendee.avatar) {
    return (
      <img
        src={attendee.avatar}
        alt=""
        className={`${size} aspect-square object-cover`}
        data-testid={`attendee-avatar-${attendee.id}`}
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      className={`flex ${size} items-center justify-center ${fill} text-lg font-bold`}
      data-testid={`attendee-avatar-${attendee.id}`}
    >
      {initials(attendee.name)}
    </span>
  );
}
